import { IColumn, ICommandBarItemProps, PanelType, SearchBox, SelectionMode, TextField, MessageBar, MessageBarType, PrimaryButton, IContextualMenuProps, Dropdown, IDropdownOption, DialogType, DefaultButton, Toggle } from '@fluentui/react';
import React from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import ListComponent from '../components/ListComponent';
import styles from '../styles/App.module.scss';
import commonStyles from '../styles/Common.module.scss';
import { ApplicationContext } from '../contexts/ApplicationContext';
import { FormMode } from '../utils/Enums';
import useObject from '../hooks/useObject';
import { IServiceLetter, ITaxGroup } from '../models';
import { FormatCurrency, SaveFileAs } from '../utils/Helpers';
import EditableNumberTextField from '../components/EditableNumberTextField';
import { IExportRequest, IServiceLetterPerShareRequest, IServiceLetterRequest, IShareControlRequest } from '../services/ExportService';
import ConfirmablePanel from '../components/ConfirmablePanel';
import FreezableDetailsList, { IFilterableColumn } from '../components/FreezableDetailsList';
import { IExport } from '../models/IExport';
import { useAuthenticationContext } from '../hooks/useAuthenticationContext';
import ErrorDialog from '../components/ErrorDialog';
import LoadingResultDialog from '../components/LoadingResultDialog';

interface IServiceLetterProps
{

}

export const ServiceLetter: React.FunctionComponent<IServiceLetterProps> = (props: React.PropsWithChildren<IServiceLetterProps>) => {
    const navigate: NavigateFunction = useNavigate();  
    const { Services } = React.useContext(ApplicationContext);

    const [allServiceletters, setAllServiceLetters] = React.useState<IServiceLetter[]>([]);  
    const [serviceLetters, setServiceLetters] = React.useState<IServiceLetter[]>([]);
    const [serviceLettersPerShare, setServiceLettersPerShare] = React.useState<IServiceLetter[]>([]);  
    const [selected, setSelected] = React.useState<IServiceLetter | undefined>(undefined);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [showPanel, setShowPanel] = React.useState<boolean>(false);
    const { value: entity, updateValue: updateEntity, setValue: setValue } = useObject<IServiceLetter>({type: "I", amount: 0, interests: 0, interestsExpense: 0, elsalgInclRenter: 0} as IServiceLetter);
    const [serviceLetterValuesPerShare, setServiceLetterValuesPerShare] = React.useState<IServiceLetter | undefined >(undefined);
    const [formMode, setFormMode] = React.useState<FormMode>(FormMode.New);
    const [taxgroups, setTaxGroups] = React.useState<ITaxGroup[]>([]);  
    const [errorDialogText, setErrorDialogText] = React.useState<string>('');
    const [showErrorDialog, setShowErrorDialog] = React.useState<boolean>(false);
    
    const [exportResult, setExportResult] = React.useState<IExport>();
    const [showExportDialog, setShowExportDialog] = React.useState<boolean>(false);
    const [exportLabel, setExportLabel] = React.useState<string>('');
    const [showLoadingDialog, setShowLoadingDialog] = React.useState<boolean>(false);

    const openErrorDialog = (message: string) => {
      setErrorDialogText(message);
      setShowErrorDialog(true);
    };

    const { getCurrentGuildStats } = useAuthenticationContext();
    // React.useEffect(() => {
    //   if(entity.amount != undefined && entity.interests != undefined && entity.interestsExpense != undefined)
    //   {
    //     updateEntity({elsalgInclRenter: entity.amount + (entity.interests - entity.interestsExpense)});
    //   }
    // }, [entity.amount, entity.interests, entity.interestsExpense]);

    const COLUMNS = [      
        {
          key: 'year',
          name: 'Skatteår',
          fieldName: 'year',
          minWidth: 100,
          maxWidth: 100,
          isResizable: false,
          filter: true
        },
        {
          key: 'amount',
          name: 'Elsalg i alt',
          fieldName: 'amount',
          minWidth: 100,
          maxWidth: 100,
          isResizable: false,
          onRender: (item: IServiceLetter) => {
            return FormatCurrency(item.amount);
          }
        },
        {
          key: 'interests',
          name: 'Renteindtægter',
          fieldName: 'interests',
          minWidth: 100,
          maxWidth: 100,
          isResizable: false,
          onRender: (item: IServiceLetter) => {
            return FormatCurrency(item.interests);
          }
        },
        {
          key: 'interestsExpense',
          name: 'Renteudgifter',
          fieldName: 'interestsExpense',
          minWidth: 100,
          maxWidth: 100,
          isResizable: true,
          onRender: (item: IServiceLetter) => {
            return FormatCurrency(item.interestsExpense);
          }
        }, 
        {
            key: 'depreciation',
            name: 'Afskrivninger',
            fieldName: 'depreciation',
            minWidth: 100,
            maxWidth: 100,
            isResizable: true,
            onRender: (item: IServiceLetter) => {
              return FormatCurrency(item.depreciation);
            }
          },    
          {
            key: 'result',
            name: 'Resultat',
            fieldName: 'result',
            minWidth: 100,
            maxWidth: 100,
            isResizable: true,
            onRender: (item: IServiceLetter) => {
              return FormatCurrency(item.result);
            }
          },       
          {
            key: 'carriedForwardDeficit',
            name: 'Overført underskud',
            fieldName: 'carriedForwardDeficit',
            minWidth: 135,
            maxWidth: 135,
            isResizable: true,
            onRender: (item: IServiceLetter) => {
              return FormatCurrency(item.carriedForwardDeficit);
            }
          },                     
          {
            key: 'extra1',
            name: 'Ekstra 1',
            fieldName: 'extra1',
            minWidth: 100,
            maxWidth: 100,
            isResizable: true,
            onRender: (item: IServiceLetter) => {
              return FormatCurrency(item.extra1);
            }
          },
          {
            key: 'extra2',
            name: 'Ekstra 2',
            fieldName: 'extra2',
            minWidth: 100,
            maxWidth: 100,
            isResizable: true,
            onRender: (item: IServiceLetter) => {
              return FormatCurrency(item.extra2);
            }
          }
      ] as IFilterableColumn[];

      React.useEffect(() => {    
        Fetch();
      }, []);

    const Fetch = async () => {
      setIsLoading(true);
        const result = await Services?.ServiceLetter.GetAll() as IServiceLetter[];                  
        
        setServiceLettersPerShare(result.filter(x => x.type === "P"));

        const resultTotal = result.filter(x => x.type === "I");
        setAllServiceLetters(resultTotal);
        setServiceLetters(resultTotal);
        setIsLoading(false);

        const taxGroups = await Services?.TaxGroup.GetAll();
        setTaxGroups(taxGroups as ITaxGroup[]);
    }
    const Reset = () => {
        setSelected(undefined);
        setShowPanel(false);
    }

const CreateOrUpdate = async() => {
  
    const result = formMode == FormMode.New ? await Services?.ServiceLetter.Create(entity) : await Services?.ServiceLetter.Update(entity);

    if(result == "OK")
      {
        Fetch();
        setShowPanel(false);     
      } else
      {
        openErrorDialog(result as string);
      }
}

    const Commands: ICommandBarItemProps[] = [
        {
            key: 'create',
            text: 'Nyt skattegrundlag',
            iconProps: { iconName: 'Add' },
            onClick: () => { 
                Reset(); setFormMode(FormMode.New); setShowPanel(true);
             },
            disabled: selected != undefined
        },
        {
          key: 'edit',
          text: 'Rediger',
          iconProps: { iconName: 'Edit' },
          onClick: () => { 
              setValue(selected as IServiceLetter); setFormMode(FormMode.Edit); setShowPanel(true);
           },
          disabled: selected === undefined
      },
        {
          key: 'downloadStakeholderFile',
          text: 'Servicebreve',
          iconProps: { iconName: 'ExcelLogo16' },
          disabled: selected == undefined,
          subMenuProps: {
            items: taxgroups.filter(x => x.name !== null).map((val, i, a) => {
              return {
                key: val.id + "",
                text: val.name,
                iconProps: { iconName: 'Download' },
                onClick: () => { 
                  setExportResult(undefined);
                  setExportLabel(val.name as string);
                  setShowExportDialog(true);
                  Services?.Export.Create({ type: 4, payload: { year: selected?.year, taxGroupId: val.id } as IServiceLetterRequest} as IExportRequest).then((exportResult : IExport) => {
                    if(exportResult !== undefined)
                    {
                      setExportResult(exportResult);                      
                    }
                  });   
                 },
              }
            })
          } as IContextualMenuProps      
        },
        {
          key: 'shareControl',
          text: 'Anpartskontrol',
          iconProps: { iconName: 'ActivateOrders' },
          subMenuProps: {
            items: taxgroups.filter(x => x.name === "Erhvervsmæssig opgørelse (Anparts regel)").map((val, i, a) => {
              return {
                key: val.id + "",
                text: val.name,
                iconProps: { iconName: 'Download' },
                onClick: () => { 
                  setShowLoadingDialog(true);
                  Services?.ShareControl.Create({ type: 5, payload: { year: selected?.year, taxGroupId: val.id } as IShareControlRequest} as IExportRequest).then((exportResult : File) => {
                    if(exportResult !== undefined)
                    {
                      SaveFileAs(window, exportResult.name, 'Udtræk til anpartskontrol', exportResult, {'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm']});
                    }
                    setShowLoadingDialog(false);
                  });   
                 },
              }
            })
          } as IContextualMenuProps,
          disabled: selected == undefined,
      },
        ];

        const CreateFarMenuItems = [
          {        
            key:"search",
            onRender:() => <SearchBox 
              placeholder="Søg efter ..." 
              className={styles.searchBox} 
              onSearch={(newValue) => { onSearch(newValue); } } 
              onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => { onSearch(newValue); }}
              onClear={ev => { onSearch(""); }}
              />
          }
        ];
    
        const onSearch = (searchTerm: string | undefined) => {
          setServiceLetters(searchTerm && searchTerm != "" ? filter(searchTerm) : allServiceletters);
        }
    
      const filter = (searchTerm: string) : IServiceLetter[] => {
        return allServiceletters.filter(i => 
          (i.year + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 || 
          (i.amount + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 ||
          (i.interests + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 || 
          (i.interestsExpense + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 || 
          (i.result + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1
        );
      }

      const parseContentToTable = (content: string) => {
        const rows = content.split('\n').filter(row => row.trim() !== '');
        const headerCells = rows[0]?.split(';') ?? [];
        const bodyRows = rows.slice(1);

        return (
            <>
              <div style={{flex:1, minHeight:0, overflowY:"auto"}}>
                <table className={commonStyles.table}>
                    <thead>
                      <tr className={`${commonStyles.tableRow} ${commonStyles.tableHeaderRow}`}>
                        {headerCells.map((cell, cellIndex) => (
                            <td key={cellIndex} className={commonStyles.tableCell} style={{position:"sticky", top:0}}><b>{cell}</b></td>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                        {bodyRows.map((row, rowIndex) => (
                            <tr key={rowIndex} className={`${commonStyles.tableRow} ${commonStyles.tableBodyRow}`}>
                                {row.split(';').map((cell, cellIndex) => (
                                    <td key={cellIndex} className={commonStyles.tableCell}>{cell}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
              </div>
              <div style={{marginTop:"10px"}}><b>Antal rækker i alt: {bodyRows.length} stk.</b></div>
            </>
        );
      };

      const update = (val: Partial<IServiceLetter>) => {
        updateEntity(val);
      }

      const shareCount : number = getCurrentGuildStats()?.shareCount as number;

      const setInputMode = async (perShare: boolean) => {
        let result = entity;
        if(perShare)
        {
          result = serviceLettersPerShare.find(x => x.year == entity.year) ?? result;

          // Get the "per share" values by dividing the total values with the share count from database
          const res : IExport = await Services?.Export.Create({type: 15, payload: { year: entity.year } as IServiceLetterPerShareRequest }) as IExport;          
          const resPerShare = JSON.parse(res.content) as IServiceLetter;
          setServiceLetterValuesPerShare(resPerShare);

          console.log(resPerShare);
          
        } else
        {
          result = serviceLetters.find(x => x.year == entity.year) ?? result;
        }

        updateEntity(result);
      }

      const entityMode_PerShare = entity.type === "P";
      
    return <div>
                <ListComponent commands={Commands} farItems={CreateFarMenuItems} title={'Servicebreve & anpartskontrol'}>
                  <FreezableDetailsList 
                                      items={serviceLetters}
                                      columns={COLUMNS}   
                                      setKey="keyServiceLetters"        
                                      selectionMode={SelectionMode.single}                    
                                      onSelectionChanged={(item:any) => { setSelected(item); }}
                                    />                
                </ListComponent>
                {showPanel && 
                    <ConfirmablePanel
                    headerText={formMode === FormMode.New ? "Nyt skattegrundlag" : "Rediger"}
                    isOpen={true}                           
                    isLightDismiss={false}
                    isBlocking={true}
                    onDismiss={(ev?: React.SyntheticEvent<HTMLElement> | KeyboardEvent) => { Reset(); }}              
                    closeButtonAriaLabel="Luk panel"
                    onRenderFooterContent={() => {
                      return <PrimaryButton text={formMode == FormMode.New ? "Opret skattegrundlag" : "Opdater skattegrundlag"} onClick={() => { CreateOrUpdate() }} disabled={entity.year === undefined || entity.amount === undefined} />
                    }}                    
                    type={PanelType.medium}>                        
                        {formMode == FormMode.New && 
                          <>
                            <MessageBar messageBarType={MessageBarType.info}>
                              Indtast alle oplysninger <b>i alt</b> for hele vindmøllelauget i det respektive skatteår. <br/>Der er {new Intl.NumberFormat('da').format(shareCount)} andele i vindmøllelauget.
                            </MessageBar>
                            <Dropdown label='Start fra tidligere år' onChange={(ev, option) => { setValue(option?.data) }} options={serviceLetters.slice(0, 5).map((val, i, a) => { return { key: val.year, text: val.year, data: val } as IDropdownOption; })}/>
                        </>
                        }                        
                        <TextField label={'Skatteår'} value={entity.year} onChange={(ev, newValue) => { updateEntity({year: newValue}) }} />
                        {formMode == FormMode.Edit && <Toggle label='Angiv værdier pr. andel (beregnede værdier vises til højre i hvert felt)' onText='Ja' offText='Nej' checked={entityMode_PerShare} onChange={(ev, checked) => { setInputMode(checked as boolean); }} />}
                        <div style={{display:"grid", gridTemplateColumns:"auto auto", gap:"10px", marginTop:"10px"}}>
                          <EditableNumberTextField required label={'Elsalg'} value={entity.amount + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.amount? serviceLetterValuesPerShare?.amount + "" : undefined} onValueChanged={(newValue: number) => { update({amount: newValue}) }} />
                          <EditableNumberTextField required label={'Renteindtægter'} value={entity.interests + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.interests ? serviceLetterValuesPerShare?.interests + "" : undefined} onValueChanged={(newValue: number) => { update({interests: newValue}) }} />
                          <EditableNumberTextField required label={'Renteudgifter'} value={entity.interestsExpense + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.interestsExpense ? serviceLetterValuesPerShare?.interestsExpense + "" : undefined} onValueChanged={(newValue: number) => { update({interestsExpense: newValue}) }} />
                          <EditableNumberTextField label={'Elsalg inkl. renter'} value={entity.elsalgInclRenter + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.elsalgInclRenter ? serviceLetterValuesPerShare?.elsalgInclRenter + "" : undefined} onValueChanged={(newValue: number) => { update({elsalgInclRenter: newValue}) }} />
                          <EditableNumberTextField label={'Resultat eksl. renter og afskrivning'} value={entity.resExclRenterAfskr + ""} suffix={entityMode_PerShare && serviceLetterValuesPerShare?.resExclRenterAfskr ? serviceLetterValuesPerShare?.resExclRenterAfskr + "" : undefined} prefix='kr.' onValueChanged={(newValue: number) => { update({resExclRenterAfskr: newValue}) }} />
                          <EditableNumberTextField label={'Afskrivninger'} value={entity.depreciation + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.depreciation ? serviceLetterValuesPerShare?.depreciation + "" : undefined} onValueChanged={(newValue: number) => { update({depreciation: newValue}) }} />                        
                          <EditableNumberTextField label={'Resultat'} value={entity.result + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.result ? serviceLetterValuesPerShare?.result + "" : undefined} onValueChanged={(newValue: number) => { update({result: newValue}) }} />
                          <EditableNumberTextField label={'Underskud fra tidligere år'} value={entity.carriedForwardDeficit + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.carriedForwardDeficit ? serviceLetterValuesPerShare?.carriedForwardDeficit + "" : undefined} onValueChanged={(newValue: number) => { update({carriedForwardDeficit: newValue}) }} />
                          <EditableNumberTextField label={'Underskud næste år'} value={entity.underskNaesteAar + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.underskNaesteAar ? serviceLetterValuesPerShare?.underskNaesteAar + "" : undefined} onValueChanged={(newValue: number) => { update({underskNaesteAar: newValue}) }} />
                          <EditableNumberTextField label={'Skattemæssigt resultat'} value={entity.skattemRes + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.skattemRes ? serviceLetterValuesPerShare?.skattemRes + "" : undefined} onValueChanged={(newValue: number) => { update({skattemRes: newValue}) }} />                          
                          <EditableNumberTextField label={'Tilgang drift (mølle)'} value={entity.tilgangDriftM + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.tilgangDriftM ? serviceLetterValuesPerShare?.tilgangDriftM + "" : undefined} onValueChanged={(newValue: number) => { update({tilgangDriftM: newValue}) }} />
                          <EditableNumberTextField label={'Afgang drift'} value={entity.afgangDriftM + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.afgangDriftM ? serviceLetterValuesPerShare?.afgangDriftM + "" : undefined} onValueChanged={(newValue: number) => { update({afgangDriftM: newValue}) }} />
                          <EditableNumberTextField label={'Aktiver'} value={entity.aktiver + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.aktiver ? serviceLetterValuesPerShare?.aktiver + "" : undefined} onValueChanged={(newValue: number) => { update({aktiver: newValue}) }} />
                          <EditableNumberTextField label={'Passiver'} value={entity.passiver + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.passiver ? serviceLetterValuesPerShare?.passiver + "" : undefined} onValueChanged={(newValue: number) => { update({passiver: newValue}) }} />
                          <EditableNumberTextField label={'Afskrivningsgruppe primo'} value={entity.afskrGrPrimo + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.afskrGrPrimo ? serviceLetterValuesPerShare?.afskrGrPrimo + "" : undefined} onValueChanged={(newValue: number) => { update({afskrGrPrimo: newValue}) }} />
                          <EditableNumberTextField label={'Afskrivningsgruppe ultimo'} value={entity.afskrGrUltimo + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.afskrGrUltimo ? serviceLetterValuesPerShare?.afskrGrUltimo + "" : undefined} onValueChanged={(newValue: number) => { update({afskrGrUltimo: newValue}) }} />
                          <EditableNumberTextField label={'Afskrivningssats'} value={entity.afskrivningssats + ""} suffix={entityMode_PerShare && serviceLetterValuesPerShare?.afskrivningssats ? serviceLetterValuesPerShare?.afskrivningssats + "" : undefined} prefix='kr.' onValueChanged={(newValue: number) => { update({afskrivningssats: newValue}) }} />
                          <EditableNumberTextField label={'Ekstra (1)'} value={entity.extra1 + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.extra1 ? serviceLetterValuesPerShare?.extra1 + "" : undefined} onValueChanged={(newValue: number) => { update({extra1: newValue}) }} />
                          <EditableNumberTextField label={'Ekstra (2)'} value={entity.extra2 + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.extra2 ? serviceLetterValuesPerShare?.extra2 + "" : undefined} onValueChanged={(newValue: number) => { update({extra2: newValue}) }} />
                          <EditableNumberTextField label={'Tilgang drift (jord)'} value={entity.tilgangDriftJ + ""} prefix='kr.' suffix={entityMode_PerShare && serviceLetterValuesPerShare?.tilgangDriftJ ? serviceLetterValuesPerShare?.tilgangDriftJ + "" : undefined} onValueChanged={(newValue: number) => { update({tilgangDriftJ: newValue}) }} />                         
                        </div>
                        <hr style={{marginTop:"20px"}}/>
                        <div style={{display:"grid", gridTemplateColumns:"auto auto", gap:"10px"}}>
                          <EditableNumberTextField label={'Bundfradrag'} disabled={entityMode_PerShare} value={entity.bundfradrag + ""} prefix='kr.' onValueChanged={(newValue: number) => { update({bundfradrag: newValue}) }} />
                          <EditableNumberTextField label={'Skatteprocent'} disabled={entityMode_PerShare} value={entity.skattepIndPct + ""} prefix='kr.' onValueChanged={(newValue: number) => { update({skattepIndPct: newValue}) }} />
                        </div>
                    </ConfirmablePanel>
                }
                {showExportDialog &&
                    <LoadingResultDialog
                      isLoading={exportResult === undefined}
                      loadingLabel="Henter data..."
                      contentHeight="530px"
                      onDismiss={() => { setShowExportDialog(false); setExportResult(undefined); }}
                      dialogContentProps={{
                        type: DialogType.normal,                        
                        title: `Forhåndsvisning - Servicebreve - ${exportLabel}`,
                        subText: 'Herunder vises indholdet af filen. Klik på "Hent fil" for at gemme filen på din computer.',
                      }}
                      modalProps={{isBlocking: true, styles: { main: { selectors: {
                        ['@media (min-width: 480px)']: {
                          width: "80vw",
                          minWidth: "80vw",
                          maxWidth: '1000px'
                        }
                      } } }}}
                      footer={<>
                        <PrimaryButton onClick={async () => { await SaveFileAs(window, exportResult!.name, 'Udtræk til servicebreve brevfletning', exportResult!.content, {'text/csv': ['.csv']}); setShowExportDialog(false); setExportResult(undefined); }} text="Hent fil" />
                        <DefaultButton onClick={() => { setShowExportDialog(false); setExportResult(undefined); }} text="Annuller" style={{marginLeft:"10px"}} />
                      </>}>
                        {exportResult !== undefined && parseContentToTable(exportResult.content)}
                    </LoadingResultDialog>
                }
                {showLoadingDialog &&
                    <LoadingResultDialog
                      isLoading={true}
                      loadingLabel="Downloader fil..."
                      onDismiss={() => { setShowLoadingDialog(false); }}
                      dialogContentProps={{ title: 'Vent venligst' }}
                      modalProps={{ isBlocking: true }} />
                }
                <ErrorDialog
                  isOpen={showErrorDialog}
                  errorText={errorDialogText}
                  onDismiss={() => setShowErrorDialog(false)}
                />
           </div>
}

export default ServiceLetter;